import { useState, useEffect, useCallback, useRef } from 'react';
import {
  SimulationChatMessage,
  RedressGuidance,
  PredictedOutcome,
  CompensationBreakdown,
} from '../types/ombudsman';
import { Precedent } from '../types/case-workspace';
import { config } from '../config';
import { getSimulation } from '../utils/api';

interface SimulationOutcomeSection {
  outcome: PredictedOutcome;
  confidence: number;
  rationale?: string;
}

interface SimulationReasoningSection {
  narrative: string;
  references?: string[];
}

export interface ToolCall {
  name: string;
  message: string;
  status: string;
}

export interface AgentProgressEvent {
  phase: string;
  status: string;
  message: string;
}

const TOOL_CALL_START_MESSAGES: Record<string, string> = {
  fetch_signal: 'Fetching complaint signal details...',
  search_similar_signals: 'Searching similar internal complaint history...',
  search_fos_precedents: 'Searching FOS precedents database...',
  get_ml_prediction: 'Getting ML model prediction...',
  get_evidence: 'Fetching attached evidence documents...',
  load_skill: 'Loading skill instructions...',
  load_skill_reference: 'Loading skill reference material...',
};

interface SimulationSections {
  outcome: SimulationOutcomeSection | null;
  summary: string | null;
  timeline: string[] | null;
  reasoning: SimulationReasoningSection | null;
  keyFactors: string[] | null;
  redressGuidance: RedressGuidance | null;
  precedents: Precedent[] | null;
  compensation: CompensationBreakdown | null;
  suggestedQuestions: string[] | null;
  activatedSkills: string[] | null;
  toolCalls: ToolCall[];
  agentProgress: AgentProgressEvent[];
}

interface UseSimulationStreamReturn {
  sections: SimulationSections;
  messages: SimulationChatMessage[];
  isStreaming: boolean;
  error: string | null;
  streamingSection: 'outcome' | 'summary' | 'what_happened' | 'reasoning' | 'redress' | 'precedents' | null;
  currentStatus: string | null;
  progressMessage: string | null;
}

export function useSimulationStream(
  simulationId: string | null
): UseSimulationStreamReturn {
  const [sections, setSections] = useState<SimulationSections>({
    outcome: null,
    summary: null,
    timeline: null,
    reasoning: null,
    keyFactors: null,
    redressGuidance: null,
    precedents: null,
    compensation: null,
    suggestedQuestions: null,
    activatedSkills: null,
    toolCalls: [],
    agentProgress: [],
  });
  const [messages, setMessages] = useState<SimulationChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingSection, setStreamingSection] = useState<'outcome' | 'summary' | 'what_happened' | 'reasoning' | 'redress' | 'precedents' | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const closeConnection = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Helper function to load existing simulation data
  const loadExistingSimulation = useCallback(async (simulationData: any) => {
    try {
      // Extract DRN references from similar_decisions if available
      let drnReferences: string[] = [];
      if (Array.isArray(simulationData.similar_decisions)) {
        drnReferences = simulationData.similar_decisions
          .map((decision: any) => decision.decision_id || decision.reference || decision.id)
          .filter((ref: any) => ref && typeof ref === 'string' && ref.startsWith('DRN-'));
      }
      
      // Also check if there's a reasoning object with references field
      const reasoningReferences = simulationData.reasoning?.references || 
                                   (simulationData.reasoning_data?.references);
      
      // Use DRNs from precedents, or reasoning references, but never key_factors
      const finalReferences = drnReferences.length > 0 
        ? drnReferences 
        : (Array.isArray(reasoningReferences) && reasoningReferences.some((r: string) => r?.startsWith('DRN-'))
            ? reasoningReferences.filter((r: string) => r?.startsWith('DRN-'))
            : []);

      const storedOutcomeRationale = getOutcomeRationaleFromSimulationData(simulationData);
      
      const loadedSections: SimulationSections = {
        outcome: simulationData.predicted_outcome && simulationData.confidence_score !== undefined
          ? {
              outcome: simulationData.predicted_outcome as PredictedOutcome,
              confidence: Math.max(0, Math.min(100, Number(simulationData.confidence_score ?? 0))),
              rationale: storedOutcomeRationale,
            }
          : null,
        summary: simulationData.summary || null,
        timeline: Array.isArray(simulationData.timeline) ? simulationData.timeline : null,
        reasoning: simulationData.reasoning
          ? {
              narrative: typeof simulationData.reasoning === 'string' 
                ? simulationData.reasoning 
                : simulationData.reasoning.narrative || simulationData.reasoning.reasoning || '',
              references: finalReferences,
            }
          : null,
        keyFactors: Array.isArray(simulationData.key_factors) ? simulationData.key_factors : null,
        redressGuidance: simulationData.redress_guidance
          ? convertRedressGuidance(simulationData.redress_guidance, simulationData.compensation_breakdown ? normalizeCompensationBreakdown(simulationData.compensation_breakdown) : null)
          : null,
        precedents: Array.isArray(simulationData.similar_decisions) ? mapPrecedents(simulationData.similar_decisions) : null,
        compensation: simulationData.compensation_breakdown
          ? normalizeCompensationBreakdown(simulationData.compensation_breakdown)
          : null,
        suggestedQuestions: Array.isArray(simulationData.suggested_questions) ? simulationData.suggested_questions : null,
        activatedSkills: normalizeActivatedSkills(
          Array.isArray(simulationData.activated_skills)
            ? simulationData.activated_skills
            : (simulationData.simulation_metadata?.activated_skills ?? null)
        ),
        toolCalls: normalizeStoredToolCalls(simulationData.simulation_metadata?.tool_calls),
        agentProgress: normalizeStoredAgentProgress(simulationData.simulation_metadata?.agent_progress),
      };

      setSections(loadedSections);
      setIsStreaming(false);
      setError(null);
    } catch (err) {
      console.error('Failed to load existing simulation data:', err);
      setError('Failed to load simulation data');
      setIsStreaming(false);
    }
  }, []);

  useEffect(() => {
    if (!simulationId) return;

    // Reset state
    setSections({
      outcome: null,
      summary: null,
      timeline: null,
      reasoning: null,
      keyFactors: null,
      redressGuidance: null,
      precedents: null,
      compensation: null,
      suggestedQuestions: null,
      activatedSkills: null,
      toolCalls: [],
      agentProgress: [],
    });
    setMessages([]);
    setError(null);
    setStreamingSection(null);
    setProgressMessage(null);
    setIsStreaming(true);

    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found');
      setIsStreaming(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const initializeSimulation = async () => {
      try {
        // First, fetch the simulation to check its status
        const simulationData = await getSimulation(simulationId);
        
        // Check if simulation is already completed
        if (simulationData.status === 'completed') {
          // Load existing data instead of streaming
          await loadExistingSimulation(simulationData);
          return;
        }

        // If status is "pending" or "processing", start streaming
        if (simulationData.status === 'pending' || simulationData.status === 'processing') {
          await handleStream();
        } else if (simulationData.status === 'failed') {
          setError(simulationData.error_message || 'Simulation failed');
          setIsStreaming(false);
        } else {
          // Unknown status, try to stream anyway
          await handleStream();
        }
      } catch (err: any) {
        console.error('Failed to initialize simulation:', err);
        setError('Failed to load simulation');
        setIsStreaming(false);
      }
    };

    const handleStream = async () => {
      // Create EventSource URL with auth token as query parameter
      // EventSource doesn't support custom headers, so we pass token in URL
      const streamUrl = `${config.API_URL}/v1/ombudsman-decisions/simulations/${simulationId}/stream`;

      const headers: HeadersInit = {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      };
      try {
        const response = await fetch(streamUrl, {
          method: 'GET',
          headers,
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          const message = response.status === 401 ? 'Authentication failed' : 'Failed to connect to simulation stream';
          setError(message);
          setIsStreaming(false);
          setCurrentStatus(null);
          setProgressMessage(null);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        const processEvent = (rawEvent: string) => {
          if (!rawEvent.trim()) return;

          const lines = rawEvent.split('\n');
          let eventType: string | null = null;
          let dataPayload = '';

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataPayload += line.slice(5).trim();
            }
          }

          if (!eventType || !dataPayload) return;

          try {
            const data = JSON.parse(dataPayload);
            dispatchEvent(eventType, data);
          } catch (err) {
            console.warn('Failed to parse SSE data', err);
          }
        };

        const dispatchEvent = (eventType: string, data: any) => {
          switch (eventType) {
            case 'status':
              setCurrentStatus(data.message ?? null);
              break;
            case 'progress':
              if (data?.message) {
                setProgressMessage(data.message);
                if (data?.phase === 'tool_call') {
                  setSections((prev) => ({
                    ...prev,
                    toolCalls: [...prev.toolCalls, {
                      name: data.name || data.tool || 'Tool',
                      message: data.message,
                      status: data.status || 'completed',
                    }],
                  }));
                } else if (data?.phase) {
                  setSections((prev) => ({
                    ...prev,
                    agentProgress: [...prev.agentProgress, {
                      phase: data.phase,
                      status: data.status || 'in_progress',
                      message: data.message,
                    }],
                  }));
                } else {
                  // Progress events without a phase go into toolCalls as general activity
                  setSections((prev) => ({
                    ...prev,
                    toolCalls: [...prev.toolCalls, {
                      name: data.name || data.tool || '',
                      message: data.message,
                      status: data.status || 'completed',
                    }],
                  }));
                }
              }
              break;
            case 'chat':
              setMessages((prev) => [
                ...prev,
                {
                  message: data.message,
                  type: 'status',
                },
              ]);
              break;
            case 'outcome':
              setStreamingSection('outcome');
              setSections((prev) => ({
                ...prev,
                outcome: {
                  outcome: data.outcome,
                  confidence: Math.max(0, Math.min(100, Number(data.confidence ?? 0))),
                  rationale: data.rationale ?? prev.outcome?.rationale,
                },
              }));
              setStreamingSection(null);
              break;
            case 'summary':
              setStreamingSection('summary');
              setSections((prev) => ({
                ...prev,
                summary: data.summary,
              }));
              setStreamingSection(null);
              break;
            case 'timeline':
              setStreamingSection('what_happened');
              setSections((prev) => ({
                ...prev,
                timeline: Array.isArray(data.events) ? data.events : data.timeline ?? null,
              }));
              setStreamingSection(null);
              break;
            case 'reasoning':
              setStreamingSection('reasoning');
              const nextNarrative = data.narrative ?? data.reasoning ?? '';
              setSections((prev) => ({
                ...prev,
                reasoning: {
                  narrative: nextNarrative,
                  // Only use references from backend, don't fallback to keyFactors
                  references: Array.isArray(data.references) && data.references.length > 0 
                    ? data.references 
                    : prev.reasoning?.references ?? [],
                },
                outcome: prev.outcome
                  ? {
                      ...prev.outcome,
                      rationale: nextNarrative || prev.outcome.rationale,
                    }
                  : prev.outcome,
              }));
              setStreamingSection(null);
              break;
            case 'key_factors':
              setSections((prev) => {
                const factors = Array.isArray(data.factors) ? data.factors : data.key_factors ?? [];
                return {
                  ...prev,
                  keyFactors: factors,
                  // Don't overwrite references with keyFactors - references should be DRNs only
                  reasoning: prev.reasoning,
                };
              });
              break;
            case 'redress':
              setStreamingSection('redress');
              setSections((prev) => {
                const fallbackComp = prev.compensation || prev.redressGuidance?.compensationBreakdown || null;
                const normalized = convertRedressGuidance(data.guidance ?? data.redress_guidance ?? null, fallbackComp);
                return {
                  ...prev,
                  redressGuidance: normalized ?? prev.redressGuidance,
                  compensation: normalized?.compensationBreakdown ?? prev.compensation,
                };
              });
              setStreamingSection(null);
              break;
            case 'compensation':
              setSections((prev) => {
                const normalized = normalizeCompensationBreakdown(data.breakdown ?? data.compensation ?? null) || prev.compensation;
                return {
                  ...prev,
                  compensation: normalized || prev.compensation,
                  redressGuidance: prev.redressGuidance
                    ? {
                        ...prev.redressGuidance,
                        compensationBreakdown: normalized || prev.redressGuidance.compensationBreakdown,
                      }
                    : prev.redressGuidance,
                };
              });
              break;
            case 'precedents':
              setStreamingSection('precedents');
              setSections((prev) => {
                const mappedPrecedents = mapPrecedents(data.precedents ?? []);
                // Extract DRNs from precedents to update references if they're missing or contain descriptive text
                const drnReferences = mappedPrecedents
                  .map(p => p.drn)
                  .filter(drn => drn && drn.startsWith('DRN-'));
                
                // Update reasoning references if they're empty or contain non-DRN text
                const currentReferences = prev.reasoning?.references ?? [];
                const hasValidDRNs = currentReferences.some(ref => ref && ref.startsWith('DRN-'));
                
                const updatedReasoning = prev.reasoning && (!hasValidDRNs && drnReferences.length > 0)
                  ? {
                      ...prev.reasoning,
                      references: drnReferences,
                    }
                  : prev.reasoning;
                
                return {
                  ...prev,
                  precedents: mappedPrecedents,
                  reasoning: updatedReasoning,
                };
              });
              setStreamingSection(null);
              break;
            case 'suggested_questions':
              setSections((prev) => ({
                ...prev,
                suggestedQuestions: Array.isArray(data.questions) ? data.questions : [],
              }));
              break;
            case 'skill_activated':
              setSections((prev) => ({
                ...prev,
                activatedSkills: normalizeActivatedSkills([...(prev.activatedSkills || []), data.name]),
              }));
              break;
            case 'complete':
              setIsStreaming(false);
              setCurrentStatus(null);
              setProgressMessage(null);
              closeConnection();
              break;
            case 'error':
              setError(data.error || 'Unknown error occurred');
              setIsStreaming(false);
              setCurrentStatus(null);
              setProgressMessage(null);
              closeConnection();
              break;
            default:
              break;
          }
        };

        while (!controller.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split('\n\n');

          // Keep the last partial chunk in the buffer
          buffer = events.pop() ?? '';

          for (const rawEvent of events) {
            processEvent(rawEvent);
          }
        }

        // Flush decoder and remaining buffered data when stream ends
        buffer += decoder.decode();
        if (buffer) {
          const remainder = buffer.trimEnd();
          if (remainder) {
            processEvent(remainder);
          }
        }

        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          if (abortControllerRef.current === controller) {
            abortControllerRef.current = null;
          }
          return;
        }
        setError('Connection to server lost');
        setIsStreaming(false);
        closeConnection();
      }
    };

    initializeSimulation();

    return () => {
      closeConnection();
    };
  }, [simulationId, closeConnection, loadExistingSimulation]);

  return {
    sections,
    messages,
    isStreaming,
    error,
    streamingSection,
    currentStatus,
    progressMessage,
  };
}

function mapPrecedents(rawPrecedents: any[]): Precedent[] {
  return (rawPrecedents ?? []).map((item, index) => {
    const similarityValue = typeof item.similarity_score === 'number'
      ? item.similarity_score
      : typeof item.similarity === 'number'
        ? item.similarity
        : undefined;

    const similarityPercent = similarityValue !== undefined
      ? Math.round((similarityValue <= 1 ? similarityValue * 100 : similarityValue))
      : undefined;

    const keyFactors = Array.isArray(item.key_factors)
      ? item.key_factors
      : item.key_factors
        ? String(item.key_factors)
            .split(/\r?\n|[;,]/)
            .map((factor: string) => factor.trim())
            .filter(Boolean)
        : [];

    return {
      drn: item.decision_id || item.reference || item.id || `precedent-${index}`,
      outcome: item.decision_outcome || item.decision_status || item.outcome || 'Unknown',
      similarity: similarityPercent,
      summary: item.summary || item.complaint_section || '',
      interpretation: item.interpretation || item.match_reasoning || item.reasoning_section || '',
      keyFactors,
      date: item.decision_date || item.date || undefined,
      url: item.url || item.link || undefined,
      pdfUrl: item.pdf_url || item.pdfUrl || undefined,
    };
  });
}

function getOutcomeRationaleFromSimulationData(simulationData: any): string | undefined {
  const explicitOutcomeRationale =
    typeof simulationData?.outcome?.rationale === 'string'
      ? simulationData.outcome.rationale
      : typeof simulationData?.predicted_outcome_rationale === 'string'
        ? simulationData.predicted_outcome_rationale
        : typeof simulationData?.rationale === 'string'
          ? simulationData.rationale
          : undefined;

  if (explicitOutcomeRationale && explicitOutcomeRationale.trim()) {
    return explicitOutcomeRationale.trim();
  }

  if (typeof simulationData?.reasoning === 'string' && simulationData.reasoning.trim()) {
    return simulationData.reasoning.trim();
  }

  const reasoningObject = simulationData?.reasoning;
  if (reasoningObject && typeof reasoningObject === 'object') {
    const objectNarrative =
      typeof reasoningObject.narrative === 'string'
        ? reasoningObject.narrative
        : typeof reasoningObject.reasoning === 'string'
          ? reasoningObject.reasoning
          : '';

    if (objectNarrative.trim()) {
      return objectNarrative.trim();
    }
  }

  const reasoningData = simulationData?.reasoning_data;
  if (reasoningData && typeof reasoningData === 'object') {
    const dataNarrative =
      typeof reasoningData.narrative === 'string'
        ? reasoningData.narrative
        : typeof reasoningData.reasoning === 'string'
          ? reasoningData.reasoning
          : '';

    if (dataNarrative.trim()) {
      return dataNarrative.trim();
    }
  }

  return undefined;
}

function normalizeActivatedSkills(raw: any): string[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }

  const seen = new Set<string>();
  const normalized = raw
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => {
      if (!value || seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });

  return normalized.length > 0 ? normalized : null;
}

function normalizeStoredToolCalls(raw: any): ToolCall[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((entry): ToolCall | null => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const legacyToolName =
        typeof entry.tool === 'string'
          ? entry.tool
          : typeof entry.name === 'string'
            ? entry.name
            : '';
      const legacyPhase =
        typeof entry.phase === 'string'
          ? entry.phase
          : typeof entry.status === 'string'
            ? entry.status
            : 'completed';
      const explicitMessage = typeof entry.message === 'string' ? entry.message : '';

      const name = legacyToolName || 'Tool';
      const status = legacyPhase || 'completed';
      const message =
        explicitMessage ||
        getStoredToolCallMessage(name, status);

      return {
        name,
        message,
        status,
      };
    })
    .filter((entry): entry is ToolCall => entry !== null);
}

function normalizeStoredAgentProgress(raw: any): AgentProgressEvent[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((entry): AgentProgressEvent | null => {
      if (!entry || typeof entry !== 'object' || typeof entry.message !== 'string') {
        return null;
      }

      const phase =
        typeof entry.phase === 'string'
          ? entry.phase
          : typeof entry.step === 'string'
            ? entry.step
            : 'analysis';
      const status = typeof entry.status === 'string' ? entry.status : 'completed';

      return {
        phase,
        status,
        message: entry.message,
      };
    })
    .filter((entry): entry is AgentProgressEvent => entry !== null);
}

function getStoredToolCallMessage(toolName: string, phase: string): string {
  if (phase === 'starting') {
    return TOOL_CALL_START_MESSAGES[toolName] || `Running ${toolName}...`;
  }

  switch (toolName) {
    case 'fetch_signal':
      return 'Loaded complaint signal';
    case 'search_similar_signals':
      return 'Loaded similar internal complaint signals';
    case 'search_fos_precedents':
      return 'Loaded similar FOS decisions';
    case 'get_ml_prediction':
      return 'Loaded ML outcome signal';
    case 'get_evidence':
      return 'Loaded evidence documents';
    case 'load_skill':
      return 'Loaded skill instructions';
    case 'load_skill_reference':
      return 'Loaded skill reference material';
    default:
      return `${toolName} completed`;
  }
}


function normalizeCompensationBreakdown(raw: any): CompensationBreakdown | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  return {
    compensation_for_loss: (raw.compensation_for_loss != null && raw.compensation_for_loss !== 'null') ? String(raw.compensation_for_loss) : 'none',
    compensation_for_distress: typeof raw.compensation_for_distress === 'number'
      ? raw.compensation_for_distress
      : Number(raw.compensation_for_distress ?? 0),
    non_financial_award: (raw.non_financial_award != null && raw.non_financial_award !== 'null') ? String(raw.non_financial_award) : 'none',
    interest: (raw.interest != null && raw.interest !== 'null') ? String(raw.interest) : 'none',
    costs_award: (raw.costs_award != null && raw.costs_award !== 'null') ? String(raw.costs_award) : 'none',
    justification: raw.justification ?? '',
  };
}

function convertRedressGuidance(raw: any, fallbackComp: CompensationBreakdown | null): RedressGuidance | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const monetary = raw.monetary_range || raw.monetaryRange;
  const monetaryRange = {
    min: monetary?.min !== undefined ? Number(monetary.min) : 0,
    max: monetary?.max !== undefined ? Number(monetary.max) : 0,
  };

  const compensation = normalizeCompensationBreakdown(raw.compensation_breakdown || raw.compensationBreakdown || fallbackComp);

  return {
    monetaryRange,
    remedies: Array.isArray(raw.remedies) ? raw.remedies : [],
    types: Array.isArray(raw.types) ? raw.types : [],
    notes: raw.notes ?? '',
    precedents: Array.isArray(raw.precedents) ? raw.precedents : [],
    compensationBreakdown: compensation || undefined,
  };
}
